import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Consultorio,
  CreateSector,
  QueueDisplayScreen,
  Sector,
  Sede,
  UpdateSector,
} from '../../../models/Entities/Turnero';

/* ------------------------------------------------------------------------------------------------
 * Contrato con la API de Offices (ver auditoría): reglas que el front debe respetar.
 * ---------------------------------------------------------------------------------------------- */

/** Valores de estado que entiende el back (minúsculas; las estadísticas comparan "disponible"). */
export const CONSULTORIO_STATUS = {
  disponible: 'disponible',
  noDisponible: 'no disponible',
} as const;

export type ConsultorioStatusKey = 'disponible' | 'ocupado' | 'noDisponible' | 'otro';

export const normalizeConsultorioStatus = (status?: string | null): ConsultorioStatusKey => {
  const value = (status || '').trim().toLowerCase();
  if (!value) return 'otro';
  if (value.includes('no disp') || value.includes('deshab') || value.includes('inactiv')) return 'noDisponible';
  if (value.includes('ocup')) return 'ocupado';
  if (value.includes('dispon') || value.includes('libre')) return 'disponible';
  return 'otro';
};

export const isDisplayPaired = (display?: QueueDisplayScreen | null): boolean =>
  Boolean((display?.fingerprint || display?.fingerPrint || '').trim());

const normalizeText = (value?: string | null): string =>
  (value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

const matches = (search: string, ...values: (string | null | undefined)[]): boolean => {
  const needle = normalizeText(search);
  if (!needle) return true;
  return values.some((v) => normalizeText(v).includes(needle));
};

/* ------------------------------------------------------------------------------------------------
 * Relaciones sede / sector / consultorio
 * ---------------------------------------------------------------------------------------------- */

export const getSectorSedeId = (sector?: Sector | null): number | undefined =>
  sector ? sector.branchId ?? sector.branch?.id ?? undefined : undefined;

export const findById = <T extends { id?: number }>(list: T[] | undefined, id?: number | null): T | undefined =>
  id === undefined || id === null ? undefined : (list || []).find((item) => item.id === id);

export const getSectoresBySede = (sectores: Sector[], sedeId?: number | null): Sector[] =>
  sedeId === undefined || sedeId === null ? sectores : sectores.filter((s) => getSectorSedeId(s) === sedeId);

/** Consultorios embebidos en el sector (`consultingRooms` con STJ, `consultorios` con Newtonsoft). */
const embeddedRooms = (sector?: Sector | null): Consultorio[] =>
  (sector?.consultingRooms || sector?.consultorios || []).filter(Boolean) as Consultorio[];

/** Ids de los consultorios que hoy pertenecen al sector, uniendo las dos fuentes disponibles. */
export const getSectorRoomIds = (sector: Sector | undefined, consultorios: Consultorio[]): number[] => {
  if (!sector || sector.id === undefined) return [];
  const ids = new Set<number>();
  embeddedRooms(sector).forEach((c) => {
    if (typeof c.id === 'number') ids.add(c.id);
  });
  consultorios.forEach((c) => {
    if (c.sectorId === sector.id && typeof c.id === 'number') ids.add(c.id);
  });
  return Array.from(ids).sort((a, b) => a - b);
};

export const getSectorRooms = (sector: Sector | undefined, consultorios: Consultorio[]): Consultorio[] => {
  const ids = getSectorRoomIds(sector, consultorios);
  const byId = new Map<number, Consultorio>();
  embeddedRooms(sector).forEach((c) => typeof c.id === 'number' && byId.set(c.id, c));
  consultorios.forEach((c) => typeof c.id === 'number' && byId.set(c.id, c));
  return ids.map((id) => byId.get(id)).filter(Boolean) as Consultorio[];
};

/** Consultorios que se pueden asignar a un sector: los libres y los que ya son del sector. */
export const getAssignableRooms = (consultorios: Consultorio[], sectorId?: number | null): Consultorio[] =>
  consultorios.filter(
    (c) => c.sectorId === null || c.sectorId === undefined || (sectorId !== undefined && sectorId !== null && c.sectorId === sectorId)
  );

/* ------------------------------------------------------------------------------------------------
 * Formularios: valores, validación y payloads
 * ---------------------------------------------------------------------------------------------- */

export type FieldErrors<K extends string> = Partial<Record<K, string>>;

// --- Sede ---------------------------------------------------------------------------------------

export interface SedeFormValues {
  name: string;
  direccion: string;
}

export const sedeToFormValues = (sede?: Sede): SedeFormValues => ({
  name: sede?.name || '',
  direccion: sede?.direccion || '',
});

export const validateSedeForm = (values: SedeFormValues, sedes: Sede[], editingId?: number): FieldErrors<'name'> => {
  const errors: FieldErrors<'name'> = {};
  const name = values.name.trim();
  if (!name) errors.name = 'Offices.validation.required';
  else if (sedes.some((s) => s.id !== editingId && normalizeText(s.name) === normalizeText(name))) {
    errors.name = 'Offices.validation.duplicateName';
  }
  return errors;
};

/** `Direccion` es string no-nullable en el DTO: null ⇒ 400, por eso se envía ''. El PUT exige id. */
export const buildSedePayload = (values: SedeFormValues, id?: number): Sede => ({
  ...(id !== undefined ? { id } : {}),
  name: values.name.trim(),
  direccion: values.direccion.trim(),
});

// --- Sector -------------------------------------------------------------------------------------

export interface SectorFormValues {
  name: string;
  number: string;
  branchId?: number;
  consultorioIds: number[];
}

export const sectorToFormValues = (
  sector: Sector | undefined,
  consultorios: Consultorio[],
  defaultSedeId?: number
): SectorFormValues => ({
  name: sector?.name || '',
  number: sector?.number || '',
  branchId: getSectorSedeId(sector) ?? defaultSedeId,
  consultorioIds: getSectorRoomIds(sector, consultorios),
});

export const validateSectorForm = (
  values: SectorFormValues,
  sectores: Sector[],
  editingId?: number
): FieldErrors<'name' | 'number' | 'branchId'> => {
  const errors: FieldErrors<'name' | 'number' | 'branchId'> = {};
  const name = values.name.trim();
  if (!name) errors.name = 'Offices.validation.required';
  else if (
    sectores.some(
      (s) => s.id !== editingId && getSectorSedeId(s) === values.branchId && normalizeText(s.name) === normalizeText(name)
    )
  ) {
    errors.name = 'Offices.validation.duplicateName';
  }
  // CreateSectorDto.Number es [Required]: sólo se pide al crear (el back no lo devuelve).
  if (editingId === undefined && !values.number.trim()) errors.number = 'Offices.validation.required';
  if (values.branchId === undefined || values.branchId === null) errors.branchId = 'Offices.validation.required';
  return errors;
};

export const buildCreateSectorPayload = (values: SectorFormValues): CreateSector => ({
  name: values.name.trim(),
  number: values.number.trim(),
  branchId: values.branchId as number,
  consultorios: [...values.consultorioIds],
});

/**
 * El PUT de sectores REEMPLAZA la lista de consultorios: si no se envía, el sector queda vacío.
 * Siempre se manda la lista completa (la actual si el usuario no la tocó).
 */
export const buildSectorUpdatePayload = (
  sector: Sector,
  consultorios: Consultorio[],
  changes: { name?: string; consultorioIds?: number[] } = {}
): UpdateSector => ({
  id: sector.id,
  name: (changes.name ?? sector.name ?? '').trim(),
  branchId: getSectorSedeId(sector) ?? null,
  consultorios: changes.consultorioIds ? [...changes.consultorioIds] : getSectorRoomIds(sector, consultorios),
});

// --- Consultorio --------------------------------------------------------------------------------

export interface ConsultorioFormValues {
  name: string;
  description: string;
  sectorId?: number | null;
  maxTurnosSimultaneos: string;
  available: boolean;
}

export const consultorioToFormValues = (consultorio?: Consultorio, defaultSectorId?: number | null): ConsultorioFormValues => ({
  name: consultorio?.name || consultorio?.number || '',
  description: consultorio?.description || '',
  sectorId: consultorio ? consultorio.sectorId ?? null : defaultSectorId ?? null,
  maxTurnosSimultaneos: String(consultorio?.maxTurnosSimultaneos ?? 1),
  available: consultorio ? normalizeConsultorioStatus(consultorio.status) !== 'noDisponible' : true,
});

export const validateConsultorioForm = (
  values: ConsultorioFormValues,
  consultorios: Consultorio[],
  editingId?: number
): FieldErrors<'name' | 'maxTurnosSimultaneos'> => {
  const errors: FieldErrors<'name' | 'maxTurnosSimultaneos'> = {};
  const name = values.name.trim();
  if (!name) errors.name = 'Offices.validation.required';
  else {
    // POST rechaza nombres repetidos en toda la base; PUT sólo dentro del mismo sector.
    const clash = consultorios.some(
      (c) =>
        c.id !== editingId &&
        normalizeText(c.name) === normalizeText(name) &&
        (editingId === undefined || (c.sectorId ?? null) === (values.sectorId ?? null))
    );
    if (clash) errors.name = 'Offices.validation.duplicateName';
  }
  const max = Number(values.maxTurnosSimultaneos);
  if (!Number.isInteger(max) || max < 1) errors.maxTurnosSimultaneos = 'Offices.validation.positiveInteger';
  return errors;
};

/**
 * El PUT de consultorios es un reemplazo completo (sector, nombre, descripción, turnos, uso) y exige
 * id en body igual a la ruta. Se parte del original para no pisar lo que el formulario no edita.
 */
export const buildConsultorioPayload = (values: ConsultorioFormValues, original?: Consultorio): Consultorio => {
  const status = original
    ? values.available
      ? normalizeConsultorioStatus(original.status) === 'noDisponible'
        ? CONSULTORIO_STATUS.disponible
        : original.status || CONSULTORIO_STATUS.disponible
      : CONSULTORIO_STATUS.noDisponible
    : values.available
    ? CONSULTORIO_STATUS.disponible
    : CONSULTORIO_STATUS.noDisponible;
  return {
    ...(original?.id !== undefined ? { id: original.id } : {}),
    name: values.name.trim(),
    description: values.description.trim() || null,
    sectorId: values.sectorId ?? null,
    status,
    maxTurnosSimultaneos: Number(values.maxTurnosSimultaneos) || 1,
    usadoPara: original?.usadoPara ?? 0,
  };
};

// --- Display ------------------------------------------------------------------------------------

export interface DisplayFormValues {
  name: string;
  description: string;
  registrationNumber: string;
  sedeId?: number | null;
  sectorId?: number | null;
  isActive: boolean;
  fingerprint: string;
}

export const displayToFormValues = (
  display: QueueDisplayScreen | undefined,
  defaults: { sedeId?: number | null; sectorId?: number | null } = {}
): DisplayFormValues => ({
  name: display?.name || '',
  description: display?.description || '',
  registrationNumber: display?.registrationNumber || '',
  sedeId: display ? display.sedeId ?? null : defaults.sedeId ?? null,
  sectorId: display ? display.sectorId ?? null : defaults.sectorId ?? null,
  isActive: display ? display.isActive ?? false : true,
  fingerprint: (display?.fingerprint || display?.fingerPrint || '').trim(),
});

export const validateDisplayForm = (
  values: DisplayFormValues,
  displays: QueueDisplayScreen[],
  sectores: Sector[],
  editingId?: number
): FieldErrors<'name' | 'registrationNumber' | 'sectorId'> => {
  const errors: FieldErrors<'name' | 'registrationNumber' | 'sectorId'> = {};
  if (!values.name.trim()) errors.name = 'Offices.validation.required';
  const code = values.registrationNumber.trim();
  if (!code) errors.registrationNumber = 'Offices.validation.required';
  else if (displays.some((d) => d.id !== editingId && normalizeText(d.registrationNumber) === normalizeText(code))) {
    errors.registrationNumber = 'Offices.validation.duplicateCode';
  }
  if (values.sectorId !== undefined && values.sectorId !== null && values.sedeId !== undefined && values.sedeId !== null) {
    const sector = findById(sectores, values.sectorId);
    const sectorSede = getSectorSedeId(sector);
    if (sector && sectorSede !== undefined && sectorSede !== values.sedeId) errors.sectorId = 'Offices.validation.sectorOtherSede';
  }
  return errors;
};

/** `registrationNumber` nunca va null (string no-nullable ⇒ 400). `fingerprint` se conserva tal cual. */
export const buildDisplayPayload = (values: DisplayFormValues, id?: number): QueueDisplayScreen => ({
  ...(id !== undefined ? { id } : {}),
  name: values.name.trim(),
  description: values.description.trim() || null,
  registrationNumber: values.registrationNumber.trim(),
  fingerprint: values.fingerprint.trim() || null,
  sedeId: values.sedeId ?? null,
  sectorId: values.sectorId ?? null,
  isActive: values.isActive,
});

/** Elegir sector fija su sede; cambiar de sede limpia un sector que no le pertenece. */
export const applyDisplayLocation = (
  values: DisplayFormValues,
  sectores: Sector[],
  change: { sedeId?: number | null; sectorId?: number | null }
): DisplayFormValues => {
  if ('sectorId' in change) {
    const sector = findById(sectores, change.sectorId);
    const sedeId = getSectorSedeId(sector);
    return { ...values, sectorId: change.sectorId ?? null, sedeId: sedeId ?? values.sedeId ?? null };
  }
  const nextSede = change.sedeId ?? null;
  const currentSector = findById(sectores, values.sectorId);
  const keepSector = currentSector && nextSede !== null ? getSectorSedeId(currentSector) === nextSede : nextSede === null;
  return { ...values, sedeId: nextSede, sectorId: keepSector ? values.sectorId ?? null : null };
};

/** Código que muestra la TV: 8 dígitos con guion (9999-9999). */
export const formatRegistrationCode = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.length <= 4 ? digits : `${digits.slice(0, 4)}-${digits.slice(4)}`;
};

export const isValidRegistrationCode = (value: string): boolean => /^\d{4}-\d{4}$/.test(value.trim());

export const findDisplayByCode = (displays: QueueDisplayScreen[], code: string): QueueDisplayScreen | undefined =>
  displays.find((d) => normalizeText(d.registrationNumber) === normalizeText(code));

/**
 * El llamado de prueba usa la asignación activa de un médico: se toma un consultorio del sector del
 * display que tenga médico asignado. Sin eso el back responde 400.
 */
export const findTestCallMedicoId = (display: QueueDisplayScreen, consultorios: Consultorio[]): string | undefined => {
  if (display.sectorId === undefined || display.sectorId === null) return undefined;
  const room = consultorios.find((c) => c.sectorId === display.sectorId && (c.medicoId || '').trim());
  return room?.medicoId?.trim() || undefined;
};

/* ------------------------------------------------------------------------------------------------
 * Listas: filtros, contadores y resumen
 * ---------------------------------------------------------------------------------------------- */

export type DisplayFilterKey = 'all' | 'active' | 'inactive' | 'unpaired';
export type ConsultorioFilterKey = 'all' | 'disponible' | 'ocupado' | 'noDisponible' | 'sinSector';

export interface LocationFilter {
  search: string;
  sedeId?: number | null;
  sectorId?: number | null;
}

const inLocation = (
  item: { sedeId?: number | null; sectorId?: number | null },
  filter: LocationFilter,
  sectores: Sector[]
): boolean => {
  if (filter.sectorId !== undefined && filter.sectorId !== null && item.sectorId !== filter.sectorId) return false;
  if (filter.sedeId !== undefined && filter.sedeId !== null) {
    const sedeId = item.sedeId ?? getSectorSedeId(findById(sectores, item.sectorId));
    if (sedeId !== filter.sedeId) return false;
  }
  return true;
};

export const displayMatchesStatus = (display: QueueDisplayScreen, status: DisplayFilterKey): boolean => {
  switch (status) {
    case 'active':
      return Boolean(display.isActive);
    case 'inactive':
      return !display.isActive;
    case 'unpaired':
      return !isDisplayPaired(display);
    default:
      return true;
  }
};

export const filterDisplays = (
  displays: QueueDisplayScreen[],
  filter: LocationFilter & { status?: DisplayFilterKey },
  sectores: Sector[]
): QueueDisplayScreen[] =>
  displays.filter((d) => {
    const sector = findById(sectores, d.sectorId);
    return (
      inLocation(d, filter, sectores) &&
      displayMatchesStatus(d, filter.status || 'all') &&
      matches(filter.search, d.name, d.description, d.registrationNumber, sector?.name)
    );
  });

export const countDisplays = (displays: QueueDisplayScreen[]): Record<DisplayFilterKey, number> => ({
  all: displays.length,
  active: displays.filter((d) => displayMatchesStatus(d, 'active')).length,
  inactive: displays.filter((d) => displayMatchesStatus(d, 'inactive')).length,
  unpaired: displays.filter((d) => displayMatchesStatus(d, 'unpaired')).length,
});

export const consultorioMatchesStatus = (consultorio: Consultorio, status: ConsultorioFilterKey): boolean => {
  if (status === 'all') return true;
  if (status === 'sinSector') return consultorio.sectorId === null || consultorio.sectorId === undefined;
  return normalizeConsultorioStatus(consultorio.status) === status;
};

export const filterConsultorios = (
  consultorios: Consultorio[],
  filter: LocationFilter & { status?: ConsultorioFilterKey },
  sectores: Sector[]
): Consultorio[] =>
  consultorios.filter((c) => {
    const sector = findById(sectores, c.sectorId);
    return (
      inLocation({ sectorId: c.sectorId }, filter, sectores) &&
      consultorioMatchesStatus(c, filter.status || 'all') &&
      matches(filter.search, c.name, c.number, c.description, sector?.name)
    );
  });

export const countConsultorios = (consultorios: Consultorio[]): Record<ConsultorioFilterKey, number> => ({
  all: consultorios.length,
  disponible: consultorios.filter((c) => consultorioMatchesStatus(c, 'disponible')).length,
  ocupado: consultorios.filter((c) => consultorioMatchesStatus(c, 'ocupado')).length,
  noDisponible: consultorios.filter((c) => consultorioMatchesStatus(c, 'noDisponible')).length,
  sinSector: consultorios.filter((c) => consultorioMatchesStatus(c, 'sinSector')).length,
});

export interface SedeGroup {
  sede: Sede;
  sectores: Sector[];
}

/** Sedes con sus sectores filtrados por texto: una sede aparece si ella o alguno de sus sectores coincide. */
export const groupSedes = (sedes: Sede[], sectores: Sector[], search: string): SedeGroup[] =>
  sedes
    .map((sede) => {
      const own = sectores.filter((s) => getSectorSedeId(s) === sede.id);
      const sedeMatches = matches(search, sede.name, sede.direccion);
      return { sede, sectores: sedeMatches ? own : own.filter((s) => matches(search, s.name, s.number)) };
    })
    .filter((group) => matches(search, group.sede.name, group.sede.direccion) || group.sectores.length > 0);

export interface OfficesSummary {
  sedes: number;
  sectores: number;
  consultorios: number;
  consultoriosDisponibles: number;
  consultoriosOcupados: number;
  consultoriosNoDisponibles: number;
  displays: number;
  displaysActivos: number;
  displaysSinVincular: number;
}

export const computeOfficesSummary = (
  sedes: Sede[],
  sectores: Sector[],
  consultorios: Consultorio[],
  displays: QueueDisplayScreen[]
): OfficesSummary => {
  const rooms = countConsultorios(consultorios);
  const screens = countDisplays(displays);
  return {
    sedes: sedes.length,
    sectores: sectores.length,
    consultorios: rooms.all,
    consultoriosDisponibles: rooms.disponible,
    consultoriosOcupados: rooms.ocupado,
    consultoriosNoDisponibles: rooms.noDisponible,
    displays: screens.all,
    displaysActivos: screens.active,
    displaysSinVincular: screens.unpaired,
  };
};

export const countSectorDisplays = (sectorId: number | undefined, displays: QueueDisplayScreen[]): number =>
  sectorId === undefined ? 0 : displays.filter((d) => d.sectorId === sectorId).length;

/* ------------------------------------------------------------------------------------------------
 * Resultado de un pedido Redux
 * ---------------------------------------------------------------------------------------------- */

/**
 * Avisa cuándo termina el pedido que disparó ESTE componente. Las banderas `success*` del store
 * quedan en `true` después de un guardado: usarlas directamente cierra el drawer siguiente sin
 * guardar. El hook sólo resuelve tras ver la transición "ocupado → libre" posterior a `arm()`
 * (o un fallo inmediato, p.ej. sin token).
 */
export const useRequestOutcome = (busy: boolean, failed: boolean, onSettled: (ok: boolean) => void) => {
  const phase = useRef<'idle' | 'armed' | 'running'>('idle');
  const callback = useRef(onSettled);
  callback.current = onSettled;
  const [armedAt, setArmedAt] = useState(0);

  useEffect(() => {
    if (phase.current === 'armed') {
      if (busy) {
        phase.current = 'running';
      } else if (failed) {
        phase.current = 'idle';
        callback.current(false);
      }
      return;
    }
    if (phase.current === 'running' && !busy) {
      phase.current = 'idle';
      callback.current(!failed);
    }
  }, [busy, failed, armedAt]);

  return useCallback(() => {
    phase.current = 'armed';
    setArmedAt((n) => n + 1);
  }, []);
};
